"use client";
import React, { useState } from "react";

import styled from "styled-components";
import useWindowSize from "@/helpers/hooks/useWindowSize";
import Link from "next/link";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import { getShowcaseImageURL, parseShowcaseTargetURI } from "./showcase.utils";
import CustomButton from "../common/button";
import WatchPlayIcon from "@/helpers/svg/watchPlayIcon.svg";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";
import "swiper/css/pagination";
import { getCMSImageURL } from "@/helpers/CMSImageURL";

const Container = styled.div`
  display: flex;
  gap: 12px;
  width: 90%;
  overflow: hidden
  max-width: 1440px;
  margin: 0 auto;
  min-height:300px;
`;

const Card = styled.div`
  background: ${({ $background }) => $background};
  background-size: cover;
  background-position: center;
  flex-grow: ${({ $active }) => ($active ? 100 : 0.1)};
  position: relative;
  height: 250px;
  border-radius: 10px;
  transition: all ease 0.85s;
  cursor: pointer;
`;

const ImageContainer = styled.div`
  position: absolute;
  top: -145px;
  left: ${({ $active, $offLeft }) =>
    $active ? ($offLeft ? "-150px" : "-60px") : $offLeft ? "-150px" : 0};
  right: ${({ $active, $offRight }) =>
    $active ? ($offRight ? "-150px" : "-60px") : $offRight ? "-150px" : 0};
  height: 400px;
  overflow: hidden;
  transition: all ease 0.85s;
  display: flex;
  justify-content: ${({ $revert }) => ($revert ? "end" : "start")};
  align-items: end;
  z-index: 99;
  padding-right: ${({ $active, $mr, $offMl }) => ($active ? ($mr ? $mr : 0) : $offMl)};
  padding-left: ${({ $active, $ml }) => ($active ? ($ml ? $ml : 0) : 0)};
  pointer-events: none;
`;

const StyledImageWrapper = styled.div`
  height: 300px;
  transform: ${({ $active }) => ($active ? "scale(1.3)" : "scale(1)")};
  transition: all ease 0.85s;
  transform-origin: bottom;
  pointer-events: none;
  position: relative;
`;

const ContentContainer = styled.div`
  overflow: hidden;
  width: 100%;
  margin: ${({ $revert }) => ($revert ? "0 0 0 auto" : "0 auto 0 0")};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 250px;
  justify-content: end;
  padding-bottom: 12px;
  align-items: ${({ $revert }) => ($revert ? "flex-start" : "flex-end")};
  padding-right: ${({ $revert }) => ($revert ? "12px" : "0")};
  padding-left: ${({ $revert }) => ($revert ? "0" : "12px")};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: ${({ $active, $revert }) =>
    $active
      ? "translateX(0)"
      : $revert
        ? "translateX(150%)"
        : "translateX(-150%)"};
  transition: all ease 0.5s;
  min-width: 300px;
`;

const Title = styled.h1`
  font-size: 2.25rem;
  color: white;
  font-weight: bold;
  margin-bottom: 12px;
  width: fit-content;
  white-space: nowrap;
`;

const BannerMaterial = ({ items }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const device = useWindowSize();

  const cardItems = [
    {
      id: 1,
      title: items[0].title,
      actionLabel: "تماشا کن",
      background:
        "radial-gradient(circle, rgba(48,170,149,1) 0%, rgba(3,101,100,1) 100%)",
      img: "/images/1.png",
      width: 879,
      height: 600,
      ml: "100px",
      mr: "110px",
      offMl: "-90px",
      revert: true,
      offRight: true,
    },
    {
      id: 2,
      title: items[1].title,
      actionLabel: "تماشا کن",
      background:
        "radial-gradient(circle, rgba(234,234,234,1) 0%, rgba(130,132,134,1) 100%)",
      img: "/images/2.png",
      width: 600,
      height: 600,
      mr: "60px",
    },
    {
      id: 3,
      title: items[2].title,
      actionLabel: "تماشا کن",
      background:
        "radial-gradient(circle, rgba(226,105,100,1) 0%, rgba(120,13,16,1) 100%)",
      img: "/images/3.png",
      width: 923,
      height: 600,
      mr: "110px",
      offLeft: true,
    },
  ];



  if (device === "LG") {
    return (
      <Container className="xl:container">
        {items?.map((item, index) => (
          <Card
            key={`banner-card-${item._id || item.id || index}`}
            $background={cardItems[index].background}
            $active={index === activeSlide}
            onMouseEnter={() => setActiveSlide(index)}
            onClick={() => setActiveSlide(index)}
          >
            <ImageContainer
              $active={index === activeSlide}
              $offLeft={cardItems[index].offLeft}
              $offRight={cardItems[index].offRight}
              $revert={cardItems[index].revert}
              $mr={cardItems[index].mr}
              $offMl={cardItems[index].offMl}
              $ml={cardItems[index].ml}
            >
              <StyledImageWrapper $active={index === activeSlide}>
                <img
                  src={getCMSImageURL(item.overlayImage, { quality: 90, width: cardItems[index].width, format: 'png' })}
                  alt={item.title}
                  width={cardItems[index].width}
                  height={cardItems[index].height}
                  style={{
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </StyledImageWrapper>
            </ImageContainer>
            <ContentContainer $revert={cardItems[index].revert}>
              <Content $active={index === activeSlide} $revert={cardItems[index].revert}>
                <Title className="[text-shadow:_0_1px_0_rgb(0_0_0_/_40%)]">
                  {item.title}
                </Title>
                <Link href={parseShowcaseTargetURI(item.actionLink)}>
                  <CustomButton
                    label={"تماشا کن"}
                    color="primary"
                    textColor="text-primary-white md:text-lg md:w-[180px] md:h-[50px]"
                    rightIcon={<WatchPlayIcon />}
                  />
                </Link>
              </Content>
            </ContentContainer>
          </Card>
        ))}
      </Container>
    );
  } else {
    return (
      <div className="w-full h-full">
        <Swiper
          id="nostalgia-swiper"
          className="lg:mx-32 rounded-xl"
          modules={[Pagination, Autoplay]}
          slidesPerView={1}
          autoplay={{ delay: 10000 }}
          pagination={{ clickable: true }}
        >
          {items?.map(({ actionLink, title, staticRenderImage, id }, index) => {
            return (
              <SwiperSlide key={"mt" + id}>
                <Link href={parseShowcaseTargetURI(actionLink)}>
                  <div className="w-[90%] mx-auto lg:hidden relative">
                    <img
                      src={getShowcaseImageURL(staticRenderImage, { quality: 90, width: 800, format: 'png' })}
                      alt={title}
                      width={800}
                      height={450}
                      style={{
                        objectFit: 'contain',
                        width: '100%',
                        height: 'auto',
                      }}
                    />
                  </div>
                </Link>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    );
  }
};

export default BannerMaterial;
